import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { resolveScopedOverride } from '@vibesites/runtime';
import {
  buildScopeContext,
  EditableCollectionBinding,
  EditableSlotGroup,
  useCollectionBinding,
  useCollectionBindingOverrides,
  useEditMode,
  usePage,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import {
  curateGalleryPhotos,
  galleryPhotoSrc,
  groupGalleryByTab,
  hasCustomGalleryOrder,
  type GalleryTab,
} from '../utils/sortGallery';

interface GalleryImage {
  image?: string | null;
  alt_text?: string;
  title?: string;
  description?: string;
  source_page_urls?: string;
  original_url?: string | null;
}

type LifestyleSlotKey =
  | 'gallery.lifestyle_image_1'
  | 'gallery.lifestyle_image_2'
  | 'gallery.lifestyle_image_3'
  | 'gallery.lifestyle_image_4'
  | 'gallery.lifestyle_image_5';

interface LifestyleSlot {
  key: LifestyleSlotKey;
  definition: SlotGroupDefinition;
  image: string;
  alt: string;
}

type GalleryDisplayItem =
  | { kind: 'photo'; photo: GalleryImage; key: string }
  | { kind: 'lifestyle'; slot: LifestyleSlot; key: string };

function lightboxCaption(photo: GalleryImage): string {
  const description = (photo.description || '').trim();
  if (description) return description;
  return (photo.alt_text || '').trim();
}

/** True when a gallery photo is already tagged as Lifestyle (ILS title / caption). */
function isLifestyleGalleryPhoto(photo: GalleryImage): boolean {
  const title = (photo.title || '').toLowerCase();
  if (title.includes('lifestyle')) return true;
  const caption = `${photo.description || ''} ${photo.alt_text || ''}`.toLowerCase();
  return /\blifestyle\b/.test(caption);
}

/**
 * Weave inserts through a CSS grid without stacking in the same column.
 *
 * Even index spacing with stride ≡ 0 (mod columns) lands every insert in one
 * vertical lane (the bug in the Property Photos screenshot). Each insert gets
 * a distinct preferred column and an even depth target; we pick the closest
 * free cell that keeps column stagger and at least ~one row of gap.
 */
function weaveEvenly(
  base: GalleryDisplayItem[],
  inserts: GalleryDisplayItem[],
  columns = 4,
): GalleryDisplayItem[] {
  if (inserts.length === 0) return base;
  if (base.length === 0) return inserts;

  const head = base[0]!;
  const rest = base.slice(1);
  const L = inserts.length;
  const finalLen = base.length + L;
  // One full row between lifestyle tiles when the gallery is long enough.
  const minGap = Math.min(columns, Math.max(2, Math.floor((finalLen - 1) / (L + 1))));

  const placeAt = new Map<number, GalleryDisplayItem>();
  const used = new Set<number>([0]); // hero stays put
  let lastPlaced = 0;

  for (let i = 0; i < L; i++) {
    // 0,3,2,1,0… — consecutive inserts never share a column; repeats are far apart.
    const preferCol = (i * 3) % columns;
    const ideal = Math.round(((i + 1) / (L + 1)) * (finalLen - 1));

    let best = -1;
    let bestScore = Number.POSITIVE_INFINITY;

    const scoreCandidate = (cand: number, columnStrict: boolean) => {
      if (cand < 1 || cand >= finalLen || used.has(cand)) return;
      if (columnStrict && cand % columns !== preferCol) return;
      // Keep Lifestyle 1→5 in reading order.
      if (cand <= lastPlaced) return;

      let gapPenalty = 0;
      for (const pos of used) {
        if (pos === 0) continue;
        const d = Math.abs(cand - pos);
        if (d < minGap) gapPenalty += (minGap - d) * 25;
        // Extra penalty for same column within two rows (the "stacking" look).
        if (cand % columns === pos % columns && d < columns * 2) {
          gapPenalty += 40;
        }
      }

      const colPenalty = columnStrict ? 0 : Math.abs((cand % columns) - preferCol) * 8;
      const score = Math.abs(cand - ideal) + gapPenalty + colPenalty;
      if (score < bestScore) {
        bestScore = score;
        best = cand;
      }
    };

    // Prefer the staggered column; relax only if that column is exhausted.
    for (let cand = 1; cand < finalLen; cand++) scoreCandidate(cand, true);
    if (best < 0) {
      bestScore = Number.POSITIVE_INFINITY;
      for (let cand = 1; cand < finalLen; cand++) scoreCandidate(cand, false);
    }

    if (best < 0) {
      for (let cand = lastPlaced + 1; cand < finalLen; cand++) {
        if (!used.has(cand)) {
          best = cand;
          break;
        }
      }
    }

    // Reading-order + column constraints can exhaust slots after lastPlaced
    // while free cells remain earlier — salvage any free non-hero cell so we
    // never silently drop a lifestyle insert.
    if (best < 0) {
      for (let cand = 1; cand < finalLen; cand++) {
        if (!used.has(cand)) {
          best = cand;
          break;
        }
      }
    }

    if (best < 0) continue;

    used.add(best);
    if (best > lastPlaced) lastPlaced = best;
    placeAt.set(best, inserts[i]!);
  }

  const result: GalleryDisplayItem[] = [];
  let restIdx = 0;
  for (let idx = 0; idx < finalLen; idx++) {
    const insert = placeAt.get(idx);
    if (insert) result.push(insert);
    else if (idx === 0) result.push(head);
    else if (restIdx < rest.length) result.push(rest[restIdx++]!);
  }
  return result;
}

/**
 * Pull lifestyle-tagged property photos out of the tour stream, combine them
 * with dedicated lifestyle slots, and re-space everything evenly so people/
 * living shots don't sit in one clump (stage-ranked lifestyle + slot inserts).
 */
function distributeLifestyleItems(
  photos: GalleryImage[],
  lifestyle: LifestyleSlot[],
): GalleryDisplayItem[] {
  const propertyPhotos: GalleryImage[] = [];
  const galleryLifestyle: GalleryImage[] = [];
  for (const photo of photos) {
    if (isLifestyleGalleryPhoto(photo)) galleryLifestyle.push(photo);
    else propertyPhotos.push(photo);
  }

  const base: GalleryDisplayItem[] = propertyPhotos.map((photo, index) => ({
    kind: 'photo',
    photo,
    key: `photo-${index}-${galleryPhotoSrc(photo)}`,
  }));

  // Alternate slot stock with any leftover gallery lifestyle so similar shots
  // aren't adjacent when both sources contribute.
  const slotItems: GalleryDisplayItem[] = lifestyle.map((slot) => ({
    kind: 'lifestyle',
    slot,
    key: slot.key,
  }));
  const galleryLifeItems: GalleryDisplayItem[] = galleryLifestyle.map((photo, index) => ({
    kind: 'photo',
    photo,
    key: `lifestyle-photo-${index}-${galleryPhotoSrc(photo)}`,
  }));

  const inserts: GalleryDisplayItem[] = [];
  const maxLen = Math.max(slotItems.length, galleryLifeItems.length);
  for (let i = 0; i < maxLen; i++) {
    if (i < slotItems.length) inserts.push(slotItems[i]!);
    if (i < galleryLifeItems.length) inserts.push(galleryLifeItems[i]!);
  }

  return weaveEvenly(base, inserts);
}

const TABS: { key: GalleryTab; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'community', label: 'Community' },
  { key: 'apartment', label: 'Apartment' },
];

const lifestyleImage1Def: SlotGroupDefinition = {
  label: 'Lifestyle Image 1',
  description: 'Required stock lifestyle photo woven into the gallery tour. Always filled from stock imagery.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: '',
      generator: {
        prompt:
          'REQUIRED — never leave_empty. Always choose a lifestyle stock imagery option for photo #1. Prefer a social / gathering moment; otherwise any other lifestyle stock imagery. ONLY stock imagery — never property photos or photo_gallery. Never logos, brand marks, floor plans, or line art.',
      },
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: '',
      generator: {
        prompt:
          'REQUIRED — never leave_empty. Write short alt text for the selected lifestyle stock imagery photo.',
        length: '3–12 words',
      },
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'REQUIRED fill — never leave_empty on image or alt. Always choose a lifestyle stock imagery option. Prefer unused lifestyle stock imagery #1 (social / gathering); if that scene is unavailable use any other lifestyle stock imagery. Never use property photos, photo_gallery, or lifestyle-tagged gallery photos. Never logos, brand marks, floor plans, or line art. Always write alt text.',
    context:
      'Lifestyle stock slot 1 of 5 in the Property Photos gallery grid, early after the hero. Mandatory image + alt from stock imagery only — never property photos, never leave_empty. Unique photo — do not reuse Lifestyle Images 2–5.',
  },
};

const lifestyleImage2Def: SlotGroupDefinition = {
  label: 'Lifestyle Image 2',
  description: 'Required stock lifestyle photo woven into the gallery tour. Always filled from stock imagery.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: '',
      generator: {
        prompt:
          'REQUIRED — never leave_empty. Always choose a lifestyle stock imagery option for photo #2 (different from #1). Prefer an active outdoor or on-the-go moment; otherwise any other lifestyle stock imagery. ONLY stock imagery — never property photos or photo_gallery. Never logos, brand marks, floor plans, or line art.',
      },
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: '',
      generator: {
        prompt:
          'REQUIRED — never leave_empty. Write short alt text for the selected lifestyle stock imagery photo.',
        length: '3–12 words',
      },
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'REQUIRED fill — never leave_empty on image or alt. Always choose a lifestyle stock imagery option. Prefer unused lifestyle stock imagery #2 (active outdoor / on-the-go); if that scene is unavailable use any other lifestyle stock imagery. Never use property photos, photo_gallery, or lifestyle-tagged gallery photos. Never logos, brand marks, floor plans, or line art. Always write alt text.',
    context:
      'Lifestyle stock slot 2 of 5 in the Property Photos gallery grid, mid-tour among community amenities. Mandatory image + alt from stock imagery only — never property photos, never leave_empty. Different photo from Lifestyle Images 1, 3, 4, and 5.',
  },
};

const lifestyleImage3Def: SlotGroupDefinition = {
  label: 'Lifestyle Image 3',
  description: 'Required stock lifestyle photo woven into the gallery tour. Always filled from stock imagery.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: '',
      generator: {
        prompt:
          'REQUIRED — never leave_empty. Always choose a lifestyle stock imagery option for photo #3 (different from #1–2). Prefer casual neighborhood leisure; otherwise any other lifestyle stock imagery. ONLY stock imagery — never property photos or photo_gallery. Never logos, brand marks, floor plans, or line art.',
      },
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: '',
      generator: {
        prompt:
          'REQUIRED — never leave_empty. Write short alt text for the selected lifestyle stock imagery photo.',
        length: '3–12 words',
      },
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'REQUIRED fill — never leave_empty on image or alt. Always choose a lifestyle stock imagery option. Prefer unused lifestyle stock imagery #3 (neighborhood leisure); if that scene is unavailable use any other lifestyle stock imagery. Never use property photos, photo_gallery, or lifestyle-tagged gallery photos. Never logos, brand marks, floor plans, or line art. Always write alt text.',
    context:
      'Lifestyle stock slot 3 of 5 in the Property Photos gallery grid, later among community / resident spaces. Mandatory image + alt from stock imagery only — never property photos, never leave_empty. Different photo from Lifestyle Images 1, 2, 4, and 5.',
  },
};

const lifestyleImage4Def: SlotGroupDefinition = {
  label: 'Lifestyle Image 4',
  description: 'Required stock lifestyle photo woven into the gallery tour. Always filled from stock imagery.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: '',
      generator: {
        prompt:
          'REQUIRED — never leave_empty. Always choose a lifestyle stock imagery option for photo #4 (different from #1–3). Prefer a relaxed at-home living moment; otherwise any other lifestyle stock imagery. ONLY stock imagery — never property photos or photo_gallery. Never logos, brand marks, floor plans, or line art.',
      },
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: '',
      generator: {
        prompt:
          'REQUIRED — never leave_empty. Write short alt text for the selected lifestyle stock imagery photo.',
        length: '3–12 words',
      },
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 3,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'REQUIRED fill — never leave_empty on image or alt. Always choose a lifestyle stock imagery option. Prefer unused lifestyle stock imagery #4 (relaxed at-home); if that scene is unavailable use any other lifestyle stock imagery. Never use property photos, photo_gallery, or lifestyle-tagged gallery photos. Never logos, brand marks, floor plans, or line art. Always write alt text.',
    context:
      'Lifestyle stock slot 4 of 5 in the Property Photos gallery grid, near the apartment experience portion of the tour. Mandatory image + alt from stock imagery only — never property photos, never leave_empty. Different photo from Lifestyle Images 1–3 and 5.',
  },
};

const lifestyleImage5Def: SlotGroupDefinition = {
  label: 'Lifestyle Image 5',
  description: 'Required stock lifestyle photo woven into the gallery tour. Always filled from stock imagery.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: '',
      generator: {
        prompt:
          'REQUIRED — never leave_empty. Always choose a lifestyle stock imagery option for photo #5 (different from #1–4). Prefer cooking, dining, or sharing a meal; otherwise any other lifestyle stock imagery. ONLY stock imagery — never property photos or photo_gallery. Never logos, brand marks, floor plans, or line art.',
      },
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: '',
      generator: {
        prompt:
          'REQUIRED — never leave_empty. Write short alt text for the selected lifestyle stock imagery photo.',
        length: '3–12 words',
      },
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 4,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'REQUIRED fill — never leave_empty on image or alt. Always choose a lifestyle stock imagery option. Prefer unused lifestyle stock imagery #5 (cooking / dining); if that scene is unavailable use any other lifestyle stock imagery. Never use property photos, photo_gallery, or lifestyle-tagged gallery photos. Never logos, brand marks, floor plans, or line art. Always write alt text.',
    context:
      'Lifestyle stock slot 5 of 5 in the Property Photos gallery grid, late among apartment / living shots. Mandatory image + alt from stock imagery only — never property photos, never leave_empty. Different photo from Lifestyle Images 1–4.',
  },
};

function displayItemSrc(item: GalleryDisplayItem): string {
  if (item.kind === 'lifestyle') return item.slot.image;
  return galleryPhotoSrc(item.photo);
}

function displayItemAlt(item: GalleryDisplayItem): string {
  if (item.kind === 'lifestyle') return item.slot.alt || 'Lifestyle photo';
  return item.photo.alt_text || item.photo.title || '';
}

function displayItemCaption(item: GalleryDisplayItem): string {
  if (item.kind === 'lifestyle') return (item.slot.alt || '').trim();
  return lightboxCaption(item.photo);
}

function LifestyleGalleryTile({
  slot,
  onOpen,
}: {
  slot: LifestyleSlot;
  onOpen: () => void;
}) {
  const src = slot.image;
  const alt = slot.alt || 'Lifestyle photo';
  const label = slot.definition.label || 'Lifestyle Image';

  const media = src ? (
    <div
      className="block h-full w-full cursor-pointer"
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={alt || 'Open lifestyle photo'}
    >
      <VibesitesImage
        url={src}
        alt={alt}
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        sizes="(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw"
      />
    </div>
  ) : (
    <span className="flex h-full w-full items-center justify-center bg-[var(--vibe-color-surface-muted,#f3f4f6)] px-3 text-center font-body text-[10px] font-bold uppercase tracking-[0.12em] text-[var(--vibe-color-text-muted)]">
      Add {label}
    </span>
  );

  // Literal groupPath per branch — schema linter matches string literals only.
  let wrapper: React.ReactNode;
  switch (slot.key) {
    case 'gallery.lifestyle_image_1':
      wrapper = (
        <EditableSlotGroup
          groupPath="gallery.lifestyle_image_1"
          definition={lifestyleImage1Def}
          className="block h-full w-full"
        >
          {media}
        </EditableSlotGroup>
      );
      break;
    case 'gallery.lifestyle_image_2':
      wrapper = (
        <EditableSlotGroup
          groupPath="gallery.lifestyle_image_2"
          definition={lifestyleImage2Def}
          className="block h-full w-full"
        >
          {media}
        </EditableSlotGroup>
      );
      break;
    case 'gallery.lifestyle_image_3':
      wrapper = (
        <EditableSlotGroup
          groupPath="gallery.lifestyle_image_3"
          definition={lifestyleImage3Def}
          className="block h-full w-full"
        >
          {media}
        </EditableSlotGroup>
      );
      break;
    case 'gallery.lifestyle_image_4':
      wrapper = (
        <EditableSlotGroup
          groupPath="gallery.lifestyle_image_4"
          definition={lifestyleImage4Def}
          className="block h-full w-full"
        >
          {media}
        </EditableSlotGroup>
      );
      break;
    case 'gallery.lifestyle_image_5':
      wrapper = (
        <EditableSlotGroup
          groupPath="gallery.lifestyle_image_5"
          definition={lifestyleImage5Def}
          className="block h-full w-full"
        >
          {media}
        </EditableSlotGroup>
      );
      break;
  }

  return (
    <div className="aspect-square overflow-hidden group gallery-item">{wrapper}</div>
  );
}

export function Gallery() {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<GalleryTab>('all');
  const editMode = useEditMode();
  const isEditMode = editMode?.isEditMode ?? false;

  const images = useCollectionBinding<GalleryImage>('gallery.images', {
    collection: 'photo_gallery',
  });
  const page = usePage();
  const collectionOverrides = useCollectionBindingOverrides();
  const customOrder = hasCustomGalleryOrder(
    resolveScopedOverride(collectionOverrides, 'gallery.images', buildScopeContext(page)),
  );

  const lifestyle1 = useSlotGroup('gallery.lifestyle_image_1', lifestyleImage1Def);
  const lifestyle2 = useSlotGroup('gallery.lifestyle_image_2', lifestyleImage2Def);
  const lifestyle3 = useSlotGroup('gallery.lifestyle_image_3', lifestyleImage3Def);
  const lifestyle4 = useSlotGroup('gallery.lifestyle_image_4', lifestyleImage4Def);
  const lifestyle5 = useSlotGroup('gallery.lifestyle_image_5', lifestyleImage5Def);

  const lifestyleSlots = useMemo<LifestyleSlot[]>(() => {
    const raw: LifestyleSlot[] = [
      {
        key: 'gallery.lifestyle_image_1',
        definition: lifestyleImage1Def,
        image: lifestyle1.image || '',
        alt: lifestyle1.alt || '',
      },
      {
        key: 'gallery.lifestyle_image_2',
        definition: lifestyleImage2Def,
        image: lifestyle2.image || '',
        alt: lifestyle2.alt || '',
      },
      {
        key: 'gallery.lifestyle_image_3',
        definition: lifestyleImage3Def,
        image: lifestyle3.image || '',
        alt: lifestyle3.alt || '',
      },
      {
        key: 'gallery.lifestyle_image_4',
        definition: lifestyleImage4Def,
        image: lifestyle4.image || '',
        alt: lifestyle4.alt || '',
      },
      {
        key: 'gallery.lifestyle_image_5',
        definition: lifestyleImage5Def,
        image: lifestyle5.image || '',
        alt: lifestyle5.alt || '',
      },
    ];
    return raw.filter((slot) => !!slot.image || isEditMode);
  }, [
    lifestyle1.image,
    lifestyle1.alt,
    lifestyle2.image,
    lifestyle2.alt,
    lifestyle3.image,
    lifestyle3.alt,
    lifestyle4.image,
    lifestyle4.alt,
    lifestyle5.image,
    lifestyle5.alt,
    isEditMode,
  ]);

  const lifestyleUrls = useMemo(
    () => new Set(lifestyleSlots.map((slot) => slot.image).filter(Boolean)),
    [lifestyleSlots],
  );

  const curated = useMemo(() => {
    const photos = images && images.length > 0 ? images : [];
    // A saved editor order is the gallery; otherwise sequence a property tour.
    return customOrder ? groupGalleryByTab(photos) : curateGalleryPhotos(photos);
  }, [images, customOrder]);

  const withoutLifestyleDupes = useCallback(
    (photos: GalleryImage[]) =>
      photos.filter((photo) => {
        const src = galleryPhotoSrc(photo);
        return !!src && !lifestyleUrls.has(src);
      }),
    [lifestyleUrls],
  );

  const displayByTab = useMemo(() => {
    const allPhotos = withoutLifestyleDupes(curated.all);
    const communityPhotos = withoutLifestyleDupes(curated.community);
    const apartmentPhotos = withoutLifestyleDupes(curated.apartment);

    return {
      all: distributeLifestyleItems(allPhotos, lifestyleSlots),
      // Lifestyle stock belongs with community / living, not unit interiors.
      community: distributeLifestyleItems(communityPhotos, lifestyleSlots),
      apartment: apartmentPhotos.map((photo, index) => ({
        kind: 'photo' as const,
        photo,
        key: `apt-${index}-${galleryPhotoSrc(photo)}`,
      })),
    };
  }, [curated, lifestyleSlots, withoutLifestyleDupes]);

  const filteredItems = displayByTab[activeTab];
  // Use `let` (not `const … = {`) so the schema static extractor does not
  // pull this into the contentSchema eval context.
  let tabCounts = {
    all: displayByTab.all.length,
    community: displayByTab.community.length,
    apartment: displayByTab.apartment.length,
  };

  const lightboxItems = useMemo(
    () => filteredItems.filter((item) => !!displayItemSrc(item)),
    [filteredItems],
  );

  // The lightbox is a full-viewport dark overlay — never show it in the editor,
  // where it covers the whole preview frame.
  const lightboxOpen = !isEditMode && selectedIndex !== null;
  const selectedItem = lightboxOpen ? lightboxItems[selectedIndex] ?? null : null;
  const caption = selectedItem ? displayItemCaption(selectedItem) : '';

  useEffect(() => {
    if (isEditMode) setSelectedIndex(null);
  }, [isEditMode]);

  const openLightboxAt = useCallback(
    (item: GalleryDisplayItem) => {
      if (isEditMode || !displayItemSrc(item)) return;
      const index = lightboxItems.findIndex((candidate) => candidate.key === item.key);
      if (index >= 0) setSelectedIndex(index);
    },
    [lightboxItems, isEditMode],
  );

  const goToPrev = useCallback(() => {
    if (selectedIndex === null || lightboxItems.length <= 1) return;
    setSelectedIndex((selectedIndex - 1 + lightboxItems.length) % lightboxItems.length);
  }, [selectedIndex, lightboxItems.length]);

  const goToNext = useCallback(() => {
    if (selectedIndex === null || lightboxItems.length <= 1) return;
    setSelectedIndex((selectedIndex + 1) % lightboxItems.length);
  }, [selectedIndex, lightboxItems.length]);

  const closeLightbox = useCallback(() => {
    setSelectedIndex(null);
  }, []);

  // Mobile swipe support — track touch start/end X to navigate the lightbox
  const touchStartX = useRef<number | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartX.current === null) return;
      const deltaX = e.changedTouches[0].clientX - touchStartX.current;
      touchStartX.current = null;
      const SWIPE_THRESHOLD = 50;
      if (deltaX < -SWIPE_THRESHOLD) goToNext();
      else if (deltaX > SWIPE_THRESHOLD) goToPrev();
    },
    [goToNext, goToPrev],
  );

  // Keyboard navigation: Escape to close, Arrow keys for prev/next
  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowLeft') goToPrev();
      else if (e.key === 'ArrowRight') goToNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, closeLightbox, goToPrev, goToNext]);

  // Lock body scroll when lightbox is open — skip in the editor so the preview
  // frame is never left unscrollable.
  useEffect(() => {
    if (isEditMode) return;
    if (lightboxOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [lightboxOpen, isEditMode]);

  return (
    <section className="py-8 md:py-12 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <EditableCollectionBinding bindingPath="gallery.images" label="Gallery Images" className="block">
          {/* Tab filters */}
          {tabCounts.all > 0 && (
            <div className="gallery-tabs">
              {TABS.map((tab) => {
                const count = tabCounts[tab.key];
                if (tab.key !== 'all' && count === 0) return null;
                return (
                  <button
                    key={tab.key}
                    className={`gallery-tab${activeTab === tab.key ? ' gallery-tab--active' : ''}`}
                    onClick={() => {
                      setActiveTab(tab.key);
                      setSelectedIndex(null);
                    }}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          )}

          {/* Photo grid */}
          {filteredItems.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {filteredItems.map((item) => {
                if (item.kind === 'lifestyle') {
                  return (
                    <LifestyleGalleryTile
                      key={item.key}
                      slot={item.slot}
                      onOpen={() => openLightboxAt(item)}
                    />
                  );
                }

                return (
                  <div
                    key={item.key}
                    className="aspect-square overflow-hidden group cursor-pointer gallery-item"
                    onClick={() => openLightboxAt(item)}
                  >
                    <VibesitesImage
                      url={galleryPhotoSrc(item.photo)}
                      alt={displayItemAlt(item)}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      sizes="(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw"
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
              <p className="font-body text-lg">Gallery images will be displayed here.</p>
            </div>
          )}
        </EditableCollectionBinding>

        {/* Schema-lint anchors — literal groupPaths (LifestyleGalleryTile also
            uses literals; anchors cover apartment-tab / empty live cases). */}
        <div style={{ display: 'none' }} aria-hidden="true">
          <EditableSlotGroup groupPath="gallery.lifestyle_image_1" definition={lifestyleImage1Def}>
            <span />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="gallery.lifestyle_image_2" definition={lifestyleImage2Def}>
            <span />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="gallery.lifestyle_image_3" definition={lifestyleImage3Def}>
            <span />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="gallery.lifestyle_image_4" definition={lifestyleImage4Def}>
            <span />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="gallery.lifestyle_image_5" definition={lifestyleImage5Def}>
            <span />
          </EditableSlotGroup>
        </div>
      </div>

      {/* Lightbox Modal — fixed-size container so all images display consistently */}
      {lightboxOpen && selectedItem && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85"
          onClick={closeLightbox}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          role="dialog"
          aria-modal="true"
          aria-label="Image lightbox"
        >
          {/* Close button — consistent SVG X icon used across theme (matches VirtualTourEmbed) */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors duration-200"
            aria-label="Close lightbox"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Image counter */}
          {lightboxItems.length > 1 && selectedIndex !== null && (
            <div className="absolute top-5 left-1/2 -translate-x-1/2 z-20 text-white/80 text-sm font-body tracking-wide">
              {selectedIndex + 1} / {lightboxItems.length}
            </div>
          )}

          {/* Previous button — consistent chevron icon used in PropertyOverview carousel */}
          {lightboxItems.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToPrev();
              }}
              className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-white/80 text-[var(--vibe-color-text)] hover:bg-white transition-all duration-200"
              aria-label="Previous image"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
              </svg>
            </button>
          )}

          {/* Next button — consistent chevron icon used in PropertyOverview carousel */}
          {lightboxItems.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
              className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-white/80 text-[var(--vibe-color-text)] hover:bg-white transition-all duration-200"
              aria-label="Next image"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
              </svg>
            </button>
          )}

          {/* Fixed-size image container — consistent dimensions regardless of image aspect ratio */}
          <div
            className="relative w-[90vw] h-[80vh] max-w-5xl flex flex-col items-center justify-center gap-3"
            onClick={(e) => e.stopPropagation()}
          >
            <VibesitesImage
              url={displayItemSrc(selectedItem)}
              alt={displayItemAlt(selectedItem)}
              className="max-w-full max-h-[calc(80vh-3rem)] w-auto h-auto object-contain rounded-sm"
              sizes="90vw"
            />
            {caption && (
              <p className="gallery-lightbox-caption max-w-full px-2 text-center text-white/90 text-sm md:text-base font-body leading-snug">
                {caption}
              </p>
            )}
          </div>
        </div>
      )}

      <style>{`
        .gallery-tabs {
          display: flex;
          justify-content: center;
          gap: 0.25rem;
          margin-bottom: 1.5rem;
        }

        .gallery-tab {
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          padding: 0.5rem 1.25rem;
          border: 1px solid var(--vibe-color-border);
          background: transparent;
          color: var(--vibe-color-text-muted);
          cursor: pointer;
          transition: background 0.2s, color 0.2s, border-color 0.2s;
        }
        .gallery-tab:first-child {
          border-radius: 3px 0 0 3px;
        }
        .gallery-tab:last-child {
          border-radius: 0 3px 3px 0;
        }
        .gallery-tab:hover {
          color: var(--vibe-color-text);
          border-color: var(--vibe-color-text-muted);
        }
        .gallery-tab--active {
          background: var(--vibe-color-primary);
          border-color: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
        }
        .gallery-tab--active:hover {
          color: var(--vibe-color-text-on-primary);
          border-color: var(--vibe-color-primary);
        }

        .gallery-item {
          animation: gallery-fade-in 0.35s ease both;
        }

        .gallery-lightbox-caption {
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.45);
        }

        @keyframes gallery-fade-in {
          from { opacity: 0; transform: scale(0.97); }
          to { opacity: 1; transform: scale(1); }
        }

        @media (prefers-reduced-motion: reduce) {
          .gallery-item { animation: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Gallery',
  displayName: 'Photo Gallery',
  tokens: {},
  slotGroups: {
    'gallery.lifestyle_image_1': lifestyleImage1Def,
    'gallery.lifestyle_image_2': lifestyleImage2Def,
    'gallery.lifestyle_image_3': lifestyleImage3Def,
    'gallery.lifestyle_image_4': lifestyleImage4Def,
    'gallery.lifestyle_image_5': lifestyleImage5Def,
  },
  collectionBindings: {
    'gallery.images': {
      label: 'Gallery Images',
      collection: 'photo_gallery',
    },
  },
};

(Gallery as any).contentSchema = contentSchema;

export default Gallery;
